import { useState, useEffect } from "react";
import { RealEstateReceptionBookFeedPresenter } from "./presenter";
import { useCityOptions } from "./hooks/useCityOptions";
import { useCitySelect } from "./hooks/useCitySelect";
import { usePrefectureOptions } from "./hooks/usePrefectureOptions";
import { useReceptionReasonOptions } from "./hooks/useReceptionReasonOptions";
import { type IFilterCondition } from "./types";
import { buildPrefectureAndCitySetter } from "./hooks/setPrefecturesWithCities";
import { useReceptionBookImportStatuses } from "./hooks/useReceptionBookImportStatuses";
import { useGridApiRef } from "@mui/x-data-grid";
import { type GridApiCommunity } from "@mui/x-data-grid/models/api/gridApiCommunity";
import {
  getStartAndEndDate,
  getSearchMinDate,
  getSearchMaxDate,
  isDateDiffOverOneYear,
} from "./utils/dateTime";
import { useStationLineOptions } from "./hooks/useStationLineOptions";
import { useStationOptions } from "./hooks/useStationOptions";
import { type ISelectObject } from "@/types/select";
import { addDays, addYears, subDays, subYears } from "date-fns/fp";
import { isValid } from "date-fns";

const RealEstateReceptionBookFeedContainer: React.FC = () => {
  const [startDate, endDate] = getStartAndEndDate();
  const minDate = getSearchMinDate();
  const maxDate = getSearchMaxDate();

  // ---
  // hooks群
  // ---

  // MUI無料版ではGridApiRefは利用できない
  const gridApiRef: React.MutableRefObject<GridApiCommunity> = useGridApiRef();

  // エラーメッセージを管理するステート変数を追加
  const [errorMessages, setErrorMessages] = useState<{
    prefectures: string;
    cities: string;
    receptionReasons: string;
    dateStart: string;
    dateEnd: string;
    sotoRange: {
      min: string;
      max: string;
      general: string;
    };
    estimateAcreageRange: {
      min: string;
      max: string;
      general: string;
    };
    estimateLandPriceRange: {
      min: string;
      max: string;
      general: string;
    };
    approximateEstimatePriceRange: {
      min: string;
      max: string;
      general: string;
    };
    farmlandAreaRange: {
      min: string;
      max: string;
      general: string;
    };
    propertyName: string;
  }>({
    prefectures: "",
    cities: "",
    receptionReasons: "",
    dateStart: "",
    dateEnd: "",
    sotoRange: { min: "", max: "", general: "" },
    estimateAcreageRange: { min: "", max: "", general: "" },
    estimateLandPriceRange: { min: "", max: "", general: "" },
    approximateEstimatePriceRange: { min: "", max: "", general: "" },
    farmlandAreaRange: { min: "", max: "", general: "" },
    propertyName: "",
  });

  const [
    legalAffairsBureauRequestDateStart,
    setLegalAffairsBureauRequestDateStart,
  ] = useState<Date | null>(startDate);
  const [
    legalAffairsBureauRequestDateEnd,
    setLegalAffairsBureauRequestDateEnd,
  ] = useState<Date | null>(endDate);
  const [
    realEstateBookTypeRensakiRenzoku,
    setRealEstateBookTypeRensakiRenzoku,
  ] = useState<boolean>(false);
  const [realEstateBookTypeTandoku, setRealEstateBookTypeTandoku] =
    useState<boolean>(false);
  const [realEstateTypeTochi, setRealEstateTypeTochi] =
    useState<boolean>(false);
  const [realEstateTypeKutate, setRealEstateTypeKutate] =
    useState<boolean>(false);
  const [realEstateTypeTatemono, setRealEstateTypeTatemono] =
    useState<boolean>(false);
  const [realEstateTypeKyotan, setRealEstateTypeKyotan] =
    useState<boolean>(false);
  const [hasTrade, setHasTrade] = useState<boolean>(false);
  const [isTowerApartment, setIsTowerApartment] = useState<boolean>(false);
  const [isOneRoom, setIsOneRoom] = useState<boolean>(false);
  const [isMansion, setIsMansion] = useState<boolean>(false);
  const [isApartment, setIsApartment] = useState<boolean>(false);
  const [isNonResidentialBuilding, setIsNonResidentialBuilding] =
    useState<boolean>(false);
  const [isPlant, setIsPlant] = useState<boolean>(false);
  const [cities, setCities] = useState<string[]>([]);
  const [prefectures, setPrefectures] = useState<string[]>([]);
  const [receptionReasons, setReceptionReasons] = useState<string[]>([]);
  const [sotoRange, setSotoRange] = useState<Record<string, number | null>>({
    min: null,
    max: null,
  });
  const [sotoFudenashi, setSotoFudenashi] = useState<boolean>(false);
  const [estimateAcreageRange, setEstimateAcreageRange] = useState<
    Record<string, number | null>
  >({
    min: null,
    max: null,
  });
  const [estimateLandPriceRange, setEstimateLandPriceRange] = useState<
    Record<string, number | null>
  >({
    min: null,
    max: null,
  });
  const [approximateEstimatePriceRange, setApproximateEstimatePriceRange] =
    useState<Record<string, number | null>>({
      min: null,
      max: null,
    });
  const [residenceOptions, setResidenceOptions] = useState<
    Record<string, boolean>
  >({
    ittei: false,
    icchuko: false,
    ichiju: false,
    nitei: false,
    nichuko: false,
    niju: false,
    denju: false,
    junju: false,
  });
  const [commercialOptions, setCommercialOptions] = useState<
    Record<string, boolean>
  >({
    kinsyo: false,
    syogyo: false,
  });
  const [industryOptions, setIndustryOptions] = useState<
    Record<string, boolean>
  >({
    junko: false,
    kogyo: false,
    kosen: false,
  });
  const [yosekiRatioRange, setYosekiRatioRange] = useState<
    Record<string, number | null>
  >({
    min: null,
    max: null,
  });
  const [kenpeiRatioRange, setKenpeiRatioRange] = useState<
    Record<string, number | null>
  >({
    min: null,
    max: null,
  });
  const [buildDateStart, setBuildDateStart] = useState<Date | null>(null);
  const [buildDateEnd, setBuildDateEnd] = useState<Date | null>(null);
  const [propertyName, setPropertyName] = useState<string>("");
  const [buildingAgeRange, setBuildingAgeRange] = useState<
    Record<string, number | null>
  >({
    min: null,
    max: null,
  });
  const [farmlandAreaRange, setFarmlandAreaRange] = useState<
    Record<string, number | null>
  >({
    min: null,
    max: null,
  });
  const [farmlandCategories, setFarmlandCategories] = useState<string[]>([]);
  const [isRenewableEnergy, setIsRenewableEnergy] = useState<boolean>(false);

  // 選択した駅項目を保持しておくstate
  const [stationSelectedValues, setStationSelectedValues] = useState<
    ISelectObject[]
  >([]);

  const [stationLine, setStationLine] = useState<number>(-1);
  const [walk, setWalk] = useState<number>(-1);

  /**
   * FilterConditionを受け取り、対応する全てのStateにSetしていく関数
   * @param filterCondition
   */
  const setAllFilterConditionStates = (
    filterCondition: IFilterCondition
  ): void => {
    setLegalAffairsBureauRequestDateStart(
      filterCondition.legalAffairsBureauRequestDateStart
    );
    setLegalAffairsBureauRequestDateEnd(
      filterCondition.legalAffairsBureauRequestDateEnd
    );
    setRealEstateBookTypeRensakiRenzoku(
      filterCondition.realEstateBookTypeRensakiRenzoku
    );
    setRealEstateBookTypeTandoku(filterCondition.realEstateBookTypeTandoku);
    setRealEstateTypeTochi(filterCondition.realEstateTypeTochi);
    setRealEstateTypeKutate(filterCondition.realEstateTypeKutate);
    setRealEstateTypeTatemono(filterCondition.realEstateTypeTatemono);
    setRealEstateTypeKyotan(filterCondition.realEstateTypeKyotan);
    setCities(filterCondition.cities);
    setPrefectures(filterCondition.prefectures);
    setReceptionReasons(filterCondition.receptionReasons);
    setSotoRange(filterCondition.sotoRange);
    setEstimateAcreageRange(filterCondition.estimateAcreageRange);
    setEstimateLandPriceRange(filterCondition.estimateLandPriceRange);
    setApproximateEstimatePriceRange(
      filterCondition.approximateEstimatePriceRange
    );
    setResidenceOptions(filterCondition.residenceOptions);
    setCommercialOptions(filterCondition.commercialOptions);
    setIndustryOptions(filterCondition.industryOptions);
    setYosekiRatioRange(filterCondition.yosekiRatioRange);
    setKenpeiRatioRange(filterCondition.kenpeiRatioRange);
    setHasTrade(filterCondition.hasTrade);
    setIsTowerApartment(filterCondition.isTowerApartment);
    setIsOneRoom(filterCondition.isOneRoom);
    setIsMansion(filterCondition.isMansion);
    setIsApartment(filterCondition.isApartment);
    setIsNonResidentialBuilding(filterCondition.isNonResidentialBuilding);
    setIsPlant(filterCondition.isPlant);
    setIsRenewableEnergy(filterCondition.isRenewableEnergy);
    setBuildDateStart(filterCondition.buildDateStart);
    setBuildDateEnd(filterCondition.buildDateEnd);
    setPropertyName(filterCondition.propertyName);
    setStationSelectedValues(filterCondition.stationSelectedValues);
    setWalk(filterCondition.walk);
    setBuildingAgeRange(filterCondition.buildingAgeRange);
    setFarmlandAreaRange(filterCondition.farmlandAreaRange);
    setFarmlandCategories(filterCondition.farmlandCategories);
    setHasTrade(filterCondition.hasTrade);
  };

  const [filterCondition, setFilterCondition] = useState<IFilterCondition>(
    () => {
      return {
        legalAffairsBureauRequestDateStart,
        legalAffairsBureauRequestDateEnd,
        realEstateBookTypeRensakiRenzoku,
        realEstateBookTypeTandoku,
        realEstateTypeTochi,
        realEstateTypeKutate,
        realEstateTypeTatemono,
        realEstateTypeKyotan,
        cities,
        prefectures,
        receptionReasons,
        sotoRange,
        estimateAcreageRange,
        estimateLandPriceRange,
        approximateEstimatePriceRange,
        residenceOptions,
        commercialOptions,
        industryOptions,
        yosekiRatioRange,
        kenpeiRatioRange,
        hasTrade,
        isTowerApartment,
        isOneRoom,
        isMansion,
        isApartment,
        isNonResidentialBuilding,
        isPlant,
        isRenewableEnergy,
        buildDateStart,
        buildDateEnd,
        propertyName,
        stationSelectedValues,
        walk,
        buildingAgeRange,
        farmlandAreaRange,
        farmlandCategories,
      };
    }
  );

  // ---
  // custom hooks群
  // ---
  const { prefectureOptions } = usePrefectureOptions();
  const { cityParams } = useCityOptions();
  const { cityOptions } = useCitySelect(prefectures, cityParams);
  const { receptionReasonOptions } = useReceptionReasonOptions();
  const importStatuses = useReceptionBookImportStatuses();

  // 路線一覧のセレクト項目
  const { stationLineOptions } = useStationLineOptions();
  // 駅一覧のセレクト項目
  const { stationOptions } = useStationOptions(stationLine);

  // ---
  // function群
  // ---
  /**
   * 日付のバリデーションを行う
   * @param date バリデーション対象の日付
   * @param dateName 日付の名前（エラーメッセージに使用）
   * @returns エラーメッセージ（空文字の場合エラーなし）
   */
  const validateDate = (date: Date | null, dateName: string): string => {
    const isDateMissing = date === null;
    const isDateOutOfRange =
      date !== null &&
      (date.getTime() < minDate.getTime() ||
        date.getTime() > maxDate.getTime());

    // 日付の有効性チェック
    const hasInvalidFormat = date !== null && !isValid(date);

    if (isDateMissing) {
      return `法務局受付日 ${dateName}を選択して下さい`;
    }
    if (hasInvalidFormat) {
      return `法務局受付日 ${dateName}を正しい日付の形式にして下さい`;
    }
    if (isDateOutOfRange) {
      return `法務局受付日 ${dateName}が範囲外です`;
    }
    return "";
  };

  /**
   * 数値フィールドのバリデーションを行う
   * @param value 数値または null
   * @param fieldName フィールド名（エラーメッセージに使用）
   * @param maxDigits 桁数制限
   * @returns エラーメッセージ（空文字の場合エラーなし）
   */
  const validateNumberField = (
    value: number | null,
    fieldName: string,
    maxDigits: number
  ): string => {
    if (value === null) {
      return "";
    }

    // 指定された桁数を超える場合はエラー
    const valueStr = String(value);
    if (valueStr.length > maxDigits) {
      return `${fieldName}は${maxDigits}桁以下で入力してください`;
    }
    return "";
  };

  /**
   * 文字列フィールドのバリデーションを行う
   * @param value 文字列
   * @param fieldName フィールド名（エラーメッセージに使用）
   * @param maxLength 最大文字数（指定しない場合は256文字）
   * @returns エラーメッセージ（空文字の場合エラーなし）
   */
  const validateStringField = (
    value: string,
    fieldName: string,
    maxLength = 256
  ): string => {
    if (value.length > maxLength) {
      return `${fieldName}は${maxLength}文字以下で入力してください`;
    }
    return "";
  };

  /**
   * 数値範囲フィールドのバリデーションを行う
   * @param range min/maxの数値範囲オブジェクト
   * @param fieldName フィールド名（エラーメッセージに使用）
   * @param maxDigits 桁数制限（指定しない場合は16桁）
   * @returns エラーオブジェクト（min, max, general）
   */
  const validateNumberRange = (
    range: Record<string, number | null>,
    fieldName: string,
    maxDigits = 16
  ): { min: string; max: string; general: string } => {
    const result = { min: "", max: "", general: "" };

    // 最小値のバリデーション
    result.min = validateNumberField(
      range.min,
      `${fieldName}(以上)`,
      maxDigits
    );

    // 最大値のバリデーション
    result.max = validateNumberField(
      range.max,
      `${fieldName}(以下)`,
      maxDigits
    );

    // 範囲の整合性チェック（最小値 > 最大値 はエラー）
    if (range.min !== null && range.max !== null && range.min > range.max) {
      result.general = `${fieldName}は以上値が以下値を超えないようにしてください`;
    }

    return result;
  };

  const handleSearch = (): void => {
    // エラーメッセージをリセット
    const newErrorMessages = {
      prefectures: "",
      cities: "",
      receptionReasons: "",
      dateStart: "",
      dateEnd: "",
      sotoRange: { min: "", max: "", general: "" },
      estimateAcreageRange: { min: "", max: "", general: "" },
      estimateLandPriceRange: { min: "", max: "", general: "" },
      approximateEstimatePriceRange: { min: "", max: "", general: "" },
      farmlandAreaRange: { min: "", max: "", general: "" },
      propertyName: "",
    };

    if (
      prefectures.length !== 1 &&
      !cities.length &&
      approximateEstimatePriceRange.min &&
      approximateEstimatePriceRange.max
    ) {
      newErrorMessages.prefectures = "都道府県は1つのみ選択してください";
    }

    if (
      !cities.length &&
      (!approximateEstimatePriceRange.min || !approximateEstimatePriceRange.max)
    ) {
      newErrorMessages.cities = "市区町村を選択して下さい";
    }

    if (!receptionReasons.length) {
      newErrorMessages.receptionReasons = "登記原因を選択して下さい";
    }

    newErrorMessages.dateStart = validateDate(
      legalAffairsBureauRequestDateStart,
      "開始"
    );
    newErrorMessages.dateEnd = validateDate(
      legalAffairsBureauRequestDateEnd,
      "終了"
    );

    // 数値範囲フィールドのバリデーション(桁数はバックエンド側に全て合わせている訳ではなく、現実的な数字にしている)
    // 外筆（9桁：一般的に1〜数百程度の範囲内で表現される。intが2,147,483,647で10桁）
    newErrorMessages.sotoRange = validateNumberRange(sotoRange, "外筆", 9);

    // 推計地積（15桁：日本の国土面積（約378,000km²）をm²に換算すると11桁）
    newErrorMessages.estimateAcreageRange = validateNumberRange(
      estimateAcreageRange,
      "推計地積",
      15
    );

    // 公示価格（15桁：2024年で日本で一番高いものが6000万で8桁）
    newErrorMessages.estimateLandPriceRange = validateNumberRange(
      estimateLandPriceRange,
      "公示価格",
      15
    );

    // 概算価格（15桁：2024年の不動産の取引市場総額が3.9兆円で13桁）
    newErrorMessages.approximateEstimatePriceRange = validateNumberRange(
      approximateEstimatePriceRange,
      "概算価格",
      15
    );

    // 農地面積（15桁：日本の耕地面積が最大で538億m²で11桁）
    newErrorMessages.farmlandAreaRange = validateNumberRange(
      farmlandAreaRange,
      "農地面積",
      15
    );

    // 文字数フィールドのバリデーション
    // マンション名
    newErrorMessages.propertyName = validateStringField(
      propertyName,
      "マンション名",
      256
    );

    // 各バリデーション結果をチェックして、エラーがあるかどうかを判定
    const hasError =
      Boolean(newErrorMessages.dateStart) ||
      Boolean(newErrorMessages.dateEnd) ||
      Boolean(newErrorMessages.propertyName) ||
      Boolean(newErrorMessages.sotoRange.min) ||
      Boolean(newErrorMessages.sotoRange.max) ||
      Boolean(newErrorMessages.sotoRange.general) ||
      Boolean(newErrorMessages.estimateAcreageRange.min) ||
      Boolean(newErrorMessages.estimateAcreageRange.max) ||
      Boolean(newErrorMessages.estimateAcreageRange.general) ||
      Boolean(newErrorMessages.estimateLandPriceRange.min) ||
      Boolean(newErrorMessages.estimateLandPriceRange.max) ||
      Boolean(newErrorMessages.estimateLandPriceRange.general) ||
      Boolean(newErrorMessages.approximateEstimatePriceRange.min) ||
      Boolean(newErrorMessages.approximateEstimatePriceRange.max) ||
      Boolean(newErrorMessages.approximateEstimatePriceRange.general) ||
      Boolean(newErrorMessages.farmlandAreaRange.min) ||
      Boolean(newErrorMessages.farmlandAreaRange.max) ||
      Boolean(newErrorMessages.farmlandAreaRange.general);

    // エラーメッセージを更新
    setErrorMessages(newErrorMessages);

    // エラーがある場合は検索処理を実行しない
    if (hasError) {
      return;
    }

    const currentFilterCondition: IFilterCondition = {
      legalAffairsBureauRequestDateStart,
      legalAffairsBureauRequestDateEnd,
      realEstateBookTypeRensakiRenzoku,
      realEstateBookTypeTandoku,
      realEstateTypeTochi,
      realEstateTypeKutate,
      realEstateTypeTatemono,
      realEstateTypeKyotan,
      cities,
      prefectures,
      receptionReasons,
      sotoRange,
      estimateAcreageRange,
      estimateLandPriceRange,
      approximateEstimatePriceRange,
      residenceOptions,
      commercialOptions,
      industryOptions,
      yosekiRatioRange,
      kenpeiRatioRange,
      hasTrade,
      stationSelectedValues,
      walk,
      isTowerApartment,
      isOneRoom,
      isMansion,
      isApartment,
      isNonResidentialBuilding,
      isPlant,
      isRenewableEnergy,
      buildDateStart,
      buildDateEnd,
      propertyName,
      buildingAgeRange,
      farmlandAreaRange,
      farmlandCategories,
    };

    // 検索の実行
    setFilterCondition(currentFilterCondition);
  };

  const setPrefectureAndCity = buildPrefectureAndCitySetter(
    cityParams,
    cities,
    setCities,
    setPrefectures
  );

  // 法務局受付日 終了の書き換え
  useEffect(() => {
    if (
      legalAffairsBureauRequestDateEnd &&
      legalAffairsBureauRequestDateStart
    ) {
      const isOneYear = isDateDiffOverOneYear(
        legalAffairsBureauRequestDateStart,
        legalAffairsBureauRequestDateEnd
      );
      if (isOneYear) {
        const date = new Date(legalAffairsBureauRequestDateStart);

        // そのまま+1年してしまうとisDateDiffOverOneYear()が一年以上経過していると判定してしまう
        // そのため一年後を(+1年-1日)と設定する
        // +364日としないのはうるう年対策のため
        const endDate = subDays(1, addYears(1, date));
        setLegalAffairsBureauRequestDateEnd(endDate);
      }
    }
  }, [legalAffairsBureauRequestDateStart]);

  // 法務局受付日 開始の書き換え
  useEffect(() => {
    if (
      legalAffairsBureauRequestDateEnd &&
      legalAffairsBureauRequestDateStart
    ) {
      const isOneYear = isDateDiffOverOneYear(
        legalAffairsBureauRequestDateStart,
        legalAffairsBureauRequestDateEnd
      );
      if (isOneYear) {
        const date = new Date(legalAffairsBureauRequestDateEnd);

        // こちらも-1年+1日とする
        const startDate = addDays(1, subYears(1, date));
        setLegalAffairsBureauRequestDateStart(startDate);
      }
    }
  }, [legalAffairsBureauRequestDateEnd]);

  return (
    <RealEstateReceptionBookFeedPresenter
      setLegalAffairsBureauRequestDateStart={
        setLegalAffairsBureauRequestDateStart
      }
      setLegalAffairsBureauRequestDateEnd={setLegalAffairsBureauRequestDateEnd}
      setRealEstateBookTypeTandoku={setRealEstateBookTypeTandoku}
      setRealEstateBookTypeRensakiRenzoku={setRealEstateBookTypeRensakiRenzoku}
      setRealEstateTypeTochi={setRealEstateTypeTochi}
      setRealEstateTypeKutate={setRealEstateTypeKutate}
      setRealEstateTypeTatemono={setRealEstateTypeTatemono}
      setRealEstateTypeKyotan={setRealEstateTypeKyotan}
      setReceptionReasons={setReceptionReasons}
      setCities={setCities}
      setPrefectures={setPrefectureAndCity}
      setStationSelectedValues={setStationSelectedValues}
      setStationLine={setStationLine}
      setWalk={setWalk}
      setSotoRange={setSotoRange}
      setEstimateAcreageRange={setEstimateAcreageRange}
      setEstimateLandPriceRange={setEstimateLandPriceRange}
      setApproximateEstimatePriceRange={setApproximateEstimatePriceRange}
      setResidenceOptions={setResidenceOptions}
      setCommercialOptions={setCommercialOptions}
      setIndustryOptions={setIndustryOptions}
      setYosekiRatioRange={setYosekiRatioRange}
      setKenpeiRatioRange={setKenpeiRatioRange}
      handleSearch={handleSearch}
      setHasTrade={setHasTrade}
      setIsTowerApartment={setIsTowerApartment}
      setIsOneRoom={setIsOneRoom}
      setIsMansion={setIsMansion}
      setIsApartment={setIsApartment}
      setIsNonResidentialBuilding={setIsNonResidentialBuilding}
      setIsPlant={setIsPlant}
      setIsRenewableEnergy={setIsRenewableEnergy}
      setBuildDateStart={setBuildDateStart}
      setBuildDateEnd={setBuildDateEnd}
      setPropertyName={setPropertyName}
      setBuildingAgeRange={setBuildingAgeRange}
      setFarmlandAreaRange={setFarmlandAreaRange}
      setFarmlandCategories={setFarmlandCategories}
      cities={cities}
      prefectures={prefectures}
      receptionReasons={receptionReasons}
      stationSelectedValues={stationSelectedValues}
      stationLine={stationLine}
      walk={walk}
      realEstateBookTypeTandoku={realEstateBookTypeTandoku}
      realEstateBookTypeRensakiRenzoku={realEstateBookTypeRensakiRenzoku}
      legalAffairsBureauRequestDateStart={legalAffairsBureauRequestDateStart}
      legalAffairsBureauRequestDateEnd={legalAffairsBureauRequestDateEnd}
      realEstateTypeTochi={realEstateTypeTochi}
      realEstateTypeKutate={realEstateTypeKutate}
      realEstateTypeTatemono={realEstateTypeTatemono}
      realEstateTypeKyotan={realEstateTypeKyotan}
      cityOptions={cityOptions}
      prefectureOptions={prefectureOptions}
      stationLineOptions={stationLineOptions}
      stationOptions={stationOptions}
      importStatuses={importStatuses}
      receptionReasonOptions={receptionReasonOptions}
      filterCondition={filterCondition}
      startDate={startDate}
      minDate={minDate}
      sotoRange={sotoRange}
      estimateAcreageRange={estimateAcreageRange}
      estimateLandPriceRange={estimateLandPriceRange}
      approximateEstimatePriceRange={approximateEstimatePriceRange}
      residenceOptions={residenceOptions}
      commercialOptions={commercialOptions}
      industryOptions={industryOptions}
      yosekiRatioRange={yosekiRatioRange}
      kenpeiRatioRange={kenpeiRatioRange}
      maxDate={maxDate}
      hasTrade={hasTrade}
      isTowerApartment={isTowerApartment}
      isOneRoom={isOneRoom}
      isMansion={isMansion}
      isApartment={isApartment}
      isNonResidentialBuilding={isNonResidentialBuilding}
      isPlant={isPlant}
      isRenewableEnergy={isRenewableEnergy}
      buildDateStart={buildDateStart}
      buildDateEnd={buildDateEnd}
      propertyName={propertyName}
      buildingAgeRange={buildingAgeRange}
      farmlandAreaRange={farmlandAreaRange}
      farmlandCategories={farmlandCategories}
      gridApiRef={gridApiRef}
      sotoFudenashi={sotoFudenashi}
      setSotoFudenashi={setSotoFudenashi}
      errorMessages={errorMessages}
    />
  );
};

export { RealEstateReceptionBookFeedContainer };
